import React, { useState, useEffect } from 'react';
import LocationMapPicker from '../components/map/LocationMapPicker';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { 
  TrashIcon, 
  CalendarIcon, 
  PhotoIcon, 
  PencilIcon, 
  XMarkIcon, 
  BuildingStorefrontIcon, 
  ClockIcon,
  CheckCircleIcon,
  PlayIcon
} from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { getCategoryColor } from '../utils/categoryColors';
import { createFormatEventDate } from '../utils/formatEventDate';

const ManageEventsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const locale = user?.language || 'en-US';
  const navigate = useNavigate();
  const formatEventDate = createFormatEventDate(locale);

  const eventTypes = [
    { key: 'sale', labelKey: 'events.event-management.event-type.sale' },
    { key: 'workshop', labelKey: 'events.event-management.event-type.workshop' },
    { key: 'community', labelKey: 'events.event-management.event-type.community' },
    { key: 'music', labelKey: 'events.event-management.event-type.music' },
    { key: 'food', labelKey: 'events.event-management.event-type.food' },
  ];


  const toDateTimeLocal = (date) => {
    const d = new Date(date);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  const [myEvents, setMyEvents] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start: toDateTimeLocal(new Date()),
    end: toDateTimeLocal(new Date(Date.now() + 3600000)),
    category: 'sale',
    businessName: '',
    recurrenceType: 'none',
    recurrenceEndDate: '',
    locationUseProvider: true,
    locationAddress: '',
    locationName: '',
    locationLat: '',
    locationLng: ''
  });
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [endUserModified, setEndUserModified] = useState(false);
  const [createEndError, setCreateEndError] = useState('');

  const [editingEvent, setEditingEvent] = useState(null);
  const [editEndUserModified, setEditEndUserModified] = useState(false);
  const [editEndError, setEditEndError] = useState('');
  const [editFormData, setEditFormData] = useState({
    title: '', description: '', start: '', end: '', category: 'sale', businessName: '',
    recurrenceType: 'none', recurrenceEndDate: '', locationUseProvider: true, locationAddress: '', locationName: '',
    locationLat: '', locationLng: ''
  });
  const [editShowMapPicker, setEditShowMapPicker] = useState(false);
  const [editSelectedImage, setEditSelectedImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [providersRes, listingsRes, eventsRes] = await Promise.all([
        api.get('/map/providers/me'),
        api.get('/map/my-listings'),
        api.get('/events')
      ]);
      const providers = Array.isArray(providersRes.data) ? providersRes.data : [providersRes.data].filter(Boolean);
      const myProviderIds = new Set(providers.map(p => p._id.toString()));
      setListings(listingsRes.data || []);
      const allEvents = Array.isArray(eventsRes.data) ? eventsRes.data : [];
      setMyEvents(allEvents.filter(e => {
        const pid = typeof e.provider === 'object' ? e.provider?._id : e.provider;
        return pid && myProviderIds.has(pid.toString());
      }));
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const getEventStatus = (start, end) => {
    const now = new Date();
    const startDate = new Date(start);
    const endDate = new Date(end);

    if (now > endDate) return { label: t('admin.status.past') || 'Past', color: 'bg-gray-100 text-gray-500', icon: CheckCircleIcon };
    if (now >= startDate && now <= endDate) return { label: t('admin.status.live') || 'Live Now', color: 'bg-green-100 text-green-700', icon: PlayIcon };
    return { label: t('events.status.upcoming') || 'Upcoming', color: 'bg-blue-100 text-blue-700', icon: ClockIcon };
  };

  const getCategoryLabel = (categoryKey) => {
    const type = eventTypes.find(t => t.key === categoryKey);
    return type ? t(type.labelKey) : categoryKey;
  };
  
  const handleImageChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isEdit) {
          setEditSelectedImage(file);
          setEditImagePreview(reader.result);
        } else {
          setSelectedImage(file);
          setImagePreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm(t('events.event-management.alerts.event-sure-delete'))) return;
    try {
      await api.delete(`/events/${eventId}`);
      setMyEvents(prev => prev.filter(e => e._id !== eventId));
    } catch (err) {
      toast.error(t('events.event-management.alerts.event-deletion-failed'));
    }
  };

  const handleEditClick = (event) => {
    const pid = typeof event.provider === 'object' ? event.provider?._id : event.provider;
    setEditingEvent(event);
    setEditFormData({
      title: event.title || '',
      description: event.description || '',
      start: event.start ? toDateTimeLocal(event.start) : '',
      end: event.end ? toDateTimeLocal(event.end) : '',
      category: event.category || 'sale',
      businessName: pid || '',
      recurrenceType: event.recurrence?.type || 'none',
      recurrenceEndDate: event.recurrence?.endDate ? toDateTimeLocal(event.recurrence.endDate).slice(0, 10) : '',
      locationUseProvider: event.location?.useProvider !== false,
      locationAddress: event.location?.address || '',
      locationName: event.location?.name || '',
      locationLat: event.location?.lat || '',
      locationLng: event.location?.lng || ''
    });
    // If the event already has coordinates, open the map picker automatically
    if (event.location?.lat && event.location?.lng) setEditShowMapPicker(true);
    else setEditShowMapPicker(false);
    setEditImagePreview(event.photoUrl || null);
    setEditSelectedImage(null);
    // Existing end time counts as user-set; don't auto-override if start changes
    setEditEndUserModified(true);
    setEditEndError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.businessName) return toast.error(t('events.event-management.alerts.please-select-service'));
    if (formData.end && formData.start && new Date(formData.end) <= new Date(formData.start)) {
      setCreateEndError(t('events.event-management.alerts.end-before-start') || 'End time must be after start time.');
      return;
    }
    setCreateEndError('');
    const data = new FormData();
    Object.entries(formData).forEach(([key, val]) => {
        if(key === 'businessName') data.append('provider', val);
        else if (key === 'start' || key === 'end') data.append(key, new Date(val).toISOString());
        else data.append(key, val);
    });
    if (selectedImage) data.append('image', selectedImage);

    try {
      setIsSubmitting(true);
      await api.post('/events', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('events.event-management.alerts.event-created'));
      setFormData({ ...formData, title: '', description: '', businessName: '' });
      setSelectedImage(null);
      setImagePreview(null);
      loadData();
    } catch (err) { toast.error(t('events.event-management.alerts.event-creation-failed')); } finally { setIsSubmitting(false); }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (editFormData.end && editFormData.start && new Date(editFormData.end) <= new Date(editFormData.start)) {
      setEditEndError(t('events.event-management.alerts.end-before-start') || 'End time must be after start time.');
      return;
    }
    setEditEndError('');
    const data = new FormData();
    data.append('provider', editFormData.businessName);
    data.append('title', editFormData.title);
    data.append('description', editFormData.description);
    data.append('start', new Date(editFormData.start).toISOString());
    data.append('end', new Date(editFormData.end).toISOString());
    data.append('category', editFormData.category);
    data.append('recurrenceType', editFormData.recurrenceType);
    if (editFormData.recurrenceEndDate) data.append('recurrenceEndDate', editFormData.recurrenceEndDate);
    data.append('locationUseProvider', String(editFormData.locationUseProvider));
    data.append('locationAddress', editFormData.locationAddress);
    data.append('locationName', editFormData.locationName);
    if (editFormData.locationLat) data.append('locationLat', editFormData.locationLat);
    if (editFormData.locationLng) data.append('locationLng', editFormData.locationLng);
    if (editSelectedImage) data.append('image', editSelectedImage);
    try {
      await api.put(`/events/${editingEvent._id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('events.event-management.alerts.event-update'));
      setEditingEvent(null);
      loadData();
    } catch (err) { toast.error(t('events.event-management.alerts.event-update-failed')); }
  };

  const normalizeKey = (str) => str ? str.toString().toLowerCase().trim() : '';

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 font-sans text-slate-900">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold tracking-tight mb-2">{t('events.event-management.title')}</h1>
        <p className="text-slate-500">{t('events.event-management.subtitle')}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* CREATE FORM */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden sticky top-4">
            <div className="bg-blue-600 px-6 py-5 text-white">
              <h2 className="text-lg font-bold flex items-center gap-2"><CalendarIcon className="h-5 w-5"/> {t('events.event-management.create-event')}</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.event-title')}</label>
                <input className="w-full border border-slate-200 p-2.5 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('map.service-description') || 'Description'}</label>
                <textarea rows="3" className="w-full border border-slate-200 p-2.5 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder=' ' />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.service')}</label>
                <select className="w-full border border-slate-200 p-2.5 rounded-xl bg-slate-50" value={formData.businessName} onChange={e => setFormData({...formData, businessName: e.target.value})} required>
                  <option value="">{t('general.select') || 'Select Service'}</option>
                  {listings.map(l => <option key={l._id} value={l._id}>{l.businessName || l.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div className="relative group">
                    <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.start')}</label>
                    <input type="datetime-local" step="60" className="w-full border border-slate-200 p-2.5 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={formData.start}
                      onChange={e => {
                        const newStart = e.target.value;
                        setFormData(prev => {
                          const updated = { ...prev, start: newStart };
                          if (!endUserModified && newStart) {
                            const endDate = new Date(newStart);
                            endDate.setMinutes(endDate.getMinutes() + 60);
                            updated.end = toDateTimeLocal(endDate);
                          }
                          return updated;
                        });
                        setCreateEndError('');
                      }} required />
                    <ClockIcon className="h-4 w-4 text-blue-500 absolute right-3 top-9 pointer-events-none" />
                </div>
                <div className="relative group">
                    <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.end')}</label>
                    <input type="datetime-local" step="60"
                      className={`w-full border p-2.5 rounded-xl text-sm focus:ring-2 outline-none ${createEndError ? 'border-red-400 focus:ring-red-300' : 'border-slate-200 focus:ring-blue-500'}`}
                      value={formData.end}
                      onChange={e => {
                        setEndUserModified(true);
                        setFormData({ ...formData, end: e.target.value });
                        setCreateEndError('');
                      }} required />
                    <ClockIcon className="h-4 w-4 text-red-400 absolute right-3 top-9 pointer-events-none" />
                    {createEndError && <p className="text-xs text-red-500 mt-1 font-semibold">{createEndError}</p>}
                </div>
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.event-type.title')}</label>
                <select className="w-full border border-slate-200 p-2.5 rounded-xl bg-slate-50 outline-none" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}>
                  {eventTypes.map(t => <option key={t.key} value={t.key}>{getCategoryLabel(t.key)}</option>)}
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.recurrence.label')}</label>
                <select className="w-full border border-slate-200 p-2.5 rounded-xl bg-slate-50 outline-none" value={formData.recurrenceType} onChange={e => setFormData({...formData, recurrenceType: e.target.value})}>
                  <option value="none">{t('events.event-management.recurrence.none')}</option>
                  <option value="weekly">{t('events.event-management.recurrence.weekly')}</option>
                  <option value="monthly">{t('events.event-management.recurrence.monthly')}</option>
                </select>
                {formData.recurrenceType !== 'none' && (
                  <div className="mt-2">
                    <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.recurrence.end-date')}</label>
                    <input type="date" className="w-full border border-slate-200 p-2.5 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={formData.recurrenceEndDate} onChange={e => setFormData({...formData, recurrenceEndDate: e.target.value})} />
                  </div>
                )}
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.location.title')}</label>
                <div className="flex gap-3 mb-2">
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="radio" name="createLocType" checked={formData.locationUseProvider} onChange={() => setFormData({...formData, locationUseProvider: true})} />
                    {t('events.event-management.location.use-provider')}
                  </label>
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="radio" name="createLocType" checked={!formData.locationUseProvider} onChange={() => setFormData({...formData, locationUseProvider: false})} />
                    {t('events.event-management.location.custom')}
                  </label>
                </div>
                {!formData.locationUseProvider && (
                  <div className="space-y-2">
                    <input className="w-full border border-slate-200 p-2.5 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder={t('events.event-management.location.name')} value={formData.locationName} onChange={e => setFormData({...formData, locationName: e.target.value})} />
                    <input className="w-full border border-slate-200 p-2.5 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder={t('events.event-management.location.address')} value={formData.locationAddress} onChange={e => setFormData({...formData, locationAddress: e.target.value})} />
                    {/* Map picker toggle */}
                    <button
                      type="button"
                      onClick={() => setShowMapPicker(p => !p)}
                      className="flex items-center gap-1.5 text-xs font-semibold text-brand-primary hover:text-brand-primary-hover transition-colors"
                    >
                      <span>{showMapPicker ? '▲' : '▼'}</span>
                      {t('events.event-management.location.pick-on-map') || 'Pick on map'}
                      {formData.locationLat && <span className="ml-1 text-green-600">✓ pinned</span>}
                    </button>
                    {showMapPicker && (
                      <LocationMapPicker
                        position={formData.locationLat ? [Number(formData.locationLat), Number(formData.locationLng)] : null}
                        onPick={({ lat, lng, address }) => setFormData(prev => ({
                          ...prev,
                          locationLat: lat,
                          locationLng: lng,
                          locationAddress: address || prev.locationAddress
                        }))}
                        onClear={() => setFormData(prev => ({ ...prev, locationLat: '', locationLng: '' }))}
                      />
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-image') || 'Event Image'}</label>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:bg-slate-50 transition-colors">
                  {imagePreview ? (
                    <div className="relative">
                      <img src={imagePreview} alt="Preview" className="max-h-32 mx-auto rounded-lg" />
                      <button type="button" onClick={() => setImagePreview(null)} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 shadow-sm hover:bg-red-600">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <PhotoIcon className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                      <span className="text-xs text-slate-400 block">{t('events.upload-image')}</span>
                      <span className="text-[10px] text-slate-300 block mt-0.5">{t('events.image-hint')}</span>
                      <input type="file" className="hidden" accept=".jpg,.jpeg,.png" onChange={(e) => handleImageChange(e, false)} />
                    </label>
                  )}
                </div>
              </div>

              <button disabled={isSubmitting} className="w-full bg-blue-600 text-white font-bold py-4 rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all active:scale-95">
                {isSubmitting ? '...' : t('events.event-management.event-button')}
              </button>
            </form>
          </div>
        </div>

        {/* LIST SECTION */}
        <div className="lg:col-span-2">
          <div className="bg-slate-100 rounded-2xl p-1.5 mb-8 flex gap-1" role="tablist">
            <button role="tab" aria-selected={activeTab === 'active'} onClick={() => setActiveTab('active')} className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'active' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>
              {t('events.event-management.active-events')}
            </button>
            <button role="tab" aria-selected={activeTab === 'past'} onClick={() => setActiveTab('past')} className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'past' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>
              {t('events.event-management.past-events')}
            </button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2].map(i => (
                <div key={i} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden animate-pulse">
                  <div className="h-44 bg-slate-100" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-slate-100 rounded w-3/4" />
                    <div className="h-3 bg-slate-100 rounded w-1/2" />
                    <div className="h-3 bg-slate-100 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : myEvents.filter(e => activeTab === 'active' ? new Date(e.end) > new Date() : new Date(e.end) < new Date()).length === 0 ? (
            <div className="text-center py-16">
              <CalendarIcon className="h-12 w-12 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-500 font-semibold">{t('events.event-management.no-events-yet')}</p>
              <p className="text-sm text-slate-400 mt-1">{t('events.event-management.no-events-yet-desc')}</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {myEvents
              .filter(e => activeTab === 'active' ? new Date(e.end) > new Date() : new Date(e.end) < new Date())
              .map(e => {
                const status = getEventStatus(e.start, e.end);
                const StatusIcon = status.icon;

                return (
                  <div key={e._id} className={`bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col overflow-hidden transition-all hover:shadow-md ${activeTab === 'past' ? 'opacity-70 grayscale-[0.3]' : ''}`}>
                    <div className="h-44 bg-slate-50 relative">
                      {e.photoUrl ? <img src={e.photoUrl} alt="" className="w-full h-full object-cover" /> : <PhotoIcon className="h-12 w-12 text-slate-200 absolute inset-0 m-auto"/>}

                     <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 shadow-sm ${status.color}`}>
                          <StatusIcon className="h-3 w-3" />
                          {status.label}
                      </div>
                      
                     <div className="absolute top-3 left-3 px-2.5 py-1 rounded-lg text-[10px] font-black tracking-widest uppercase shadow-sm" 
                           style={{ 
                               backgroundColor: getCategoryColor(e.category).primary,
                               color: getCategoryColor(e.category).textOnPrimary 
                           }}>
                        {getCategoryLabel(e.category)}
                      </div>
                    </div>

                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-bold text-slate-900 mb-3 line-clamp-1">{e.title}</h3>
                      {e.description && (
                          <p className="text-xs text-slate-400 italic mb-3 line-clamp-2">{e.description}</p>
                      )}
                      <div className="space-y-3 text-[12px] text-slate-500 mb-5 flex-1">
                        <div className="flex items-center gap-2 font-semibold text-slate-700"><BuildingStorefrontIcon className="h-4 w-4 text-blue-500"/> {e.provider?.businessName}</div>
                        <div className="flex items-center gap-2"><ClockIcon className="h-4 w-4 text-blue-400"/> {formatEventDate(e.start, e.end)}</div>
                      </div>
                      <div className="flex gap-2 pt-4 border-t border-slate-50">
                        <button onClick={() => handleEditClick(e)} className="flex-1 bg-slate-50 text-slate-700 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors"><PencilIcon className="h-3.5 w-3.5"/> {t('general.edit')}</button>
                        <button onClick={() => handleDelete(e._id)} className="p-2.5 text-red-500 hover:bg-red-50 rounded-xl transition-colors"><TrashIcon className="h-5 w-5"/></button>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          )}
        </div>
      </div>
      
      {/* EDIT MODAL */}
      {editingEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="h-16 flex justify-between items-center px-6 bg-blue-600 rounded-t-2xl text-white">
              <h2 className="text-xl font-bold">{t('map.edit-service')}</h2>
              <button onClick={() => setEditingEvent(null)}><XMarkIcon className="h-6 w-6" /></button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.event-title')}</label>
                <input className="w-full border border-slate-200 p-2.5 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all" value={editFormData.title} onChange={e => setEditFormData({...editFormData, title: e.target.value})} required />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.description')}</label>
                <textarea rows="4" className="w-full border border-slate-300 p-2 rounded-lg resize-none text-sm" value={editFormData.description} onChange={e => setEditFormData({...editFormData, description: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="relative group">
                  <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.start')}</label>
                  <input type="datetime-local" step="60" className="w-full border border-slate-200 p-2.5 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={editFormData.start}
                    onChange={e => {
                      const newStart = e.target.value;
                      setEditFormData(prev => {
                        const updated = { ...prev, start: newStart };
                        if (!editEndUserModified && newStart) {
                          const endDate = new Date(newStart);
                          endDate.setMinutes(endDate.getMinutes() + 60);
                          updated.end = toDateTimeLocal(endDate);
                        }
                        return updated;
                      });
                      setEditEndError('');
                    }} required />
                  <ClockIcon className="h-4 w-4 text-blue-500 absolute right-3 top-9 pointer-events-none" />
                </div>
                <div className="relative group">
                  <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.end')}</label>
                  <input type="datetime-local" step="60"
                    className={`w-full border p-2.5 rounded-xl text-sm focus:ring-2 outline-none ${editEndError ? 'border-red-400 focus:ring-red-300' : 'border-slate-200 focus:ring-blue-500'}`}
                    value={editFormData.end}
                    onChange={e => {
                      setEditEndUserModified(true);
                      setEditFormData({ ...editFormData, end: e.target.value });
                      setEditEndError('');
                    }} required />
                  <ClockIcon className="h-4 w-4 text-red-400 absolute right-3 top-9 pointer-events-none" />
                  {editEndError && <p className="text-xs text-red-500 mt-1 font-semibold">{editEndError}</p>}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.service')}</label>
                <select className="w-full border border-slate-200 p-2.5 rounded-xl bg-slate-50" value={editFormData.businessName} onChange={e => setEditFormData({...editFormData, businessName: e.target.value})} required>
                  <option value="">{t('general.select') || 'Select Service'}</option>
                  {listings.map(l => <option key={l._id} value={l._id}>{l.businessName || l.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.event-type.title')}</label>
                <select className="w-full border border-slate-300 p-2 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" value={editFormData.category} onChange={e => setEditFormData({...editFormData, category: e.target.value})}>
                  {eventTypes.map(t => <option key={t.key} value={t.key}>{getCategoryLabel(t.key)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.recurrence.label')}</label>
                <select className="w-full border border-slate-300 p-2 rounded-lg" value={editFormData.recurrenceType} onChange={e => setEditFormData({...editFormData, recurrenceType: e.target.value})}>
                  <option value="none">{t('events.event-management.recurrence.none')}</option>
                  <option value="weekly">{t('events.event-management.recurrence.weekly')}</option>
                  <option value="monthly">{t('events.event-management.recurrence.monthly')}</option>
                </select>
                {editFormData.recurrenceType !== 'none' && (
                  <div className="mt-2">
                    <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.recurrence.end-date')}</label>
                    <input type="date" className="w-full border border-slate-300 p-2 rounded-lg text-sm" value={editFormData.recurrenceEndDate} onChange={e => setEditFormData({...editFormData, recurrenceEndDate: e.target.value})} />
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-management.location.title')}</label>
                <div className="flex gap-3 mb-2">
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="radio" name="editLocType" checked={editFormData.locationUseProvider} onChange={() => setEditFormData({...editFormData, locationUseProvider: true})} />
                    {t('events.event-management.location.use-provider')}
                  </label>
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="radio" name="editLocType" checked={!editFormData.locationUseProvider} onChange={() => setEditFormData({...editFormData, locationUseProvider: false})} />
                    {t('events.event-management.location.custom')}
                  </label>
                </div>
                {!editFormData.locationUseProvider && (
                  <div className="space-y-2">
                    <input className="w-full border border-slate-300 p-2 rounded-lg text-sm" placeholder={t('events.event-management.location.name')} value={editFormData.locationName} onChange={e => setEditFormData({...editFormData, locationName: e.target.value})} />
                    <input className="w-full border border-slate-300 p-2 rounded-lg text-sm" placeholder={t('events.event-management.location.address')} value={editFormData.locationAddress} onChange={e => setEditFormData({...editFormData, locationAddress: e.target.value})} />
                    {/* Map picker toggle */}
                    <button
                      type="button"
                      onClick={() => setEditShowMapPicker(p => !p)}
                      className="flex items-center gap-1.5 text-xs font-semibold text-brand-primary hover:text-brand-primary-hover transition-colors"
                    >
                      <span>{editShowMapPicker ? '▲' : '▼'}</span>
                      {t('events.event-management.location.pick-on-map') || 'Pick on map'}
                      {editFormData.locationLat && <span className="ml-1 text-green-600">✓ pinned</span>}
                    </button>
                    {editShowMapPicker && (
                      <LocationMapPicker
                        position={editFormData.locationLat ? [Number(editFormData.locationLat), Number(editFormData.locationLng)] : null}
                        onPick={({ lat, lng, address }) => setEditFormData(prev => ({
                          ...prev,
                          locationLat: lat,
                          locationLng: lng,
                          locationAddress: address || prev.locationAddress
                        }))}
                        onClear={() => setEditFormData(prev => ({ ...prev, locationLat: '', locationLng: '' }))}
                      />
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">{t('events.event-image') || 'Event Image'}</label>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:bg-slate-50 transition-colors">
                  {editImagePreview ? (
                    <div className="relative">
                      <img src={editImagePreview} alt="Preview" className="max-h-32 mx-auto rounded-lg" />
                      <button type="button" onClick={() => setEditImagePreview(null)} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 shadow-sm hover:bg-red-600">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <PhotoIcon className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                      <span className="text-xs text-slate-400 block">{t('events.upload-image')}</span>
                      <span className="text-[10px] text-slate-300 block mt-0.5">{t('events.image-hint')}</span>
                      <input type="file" className="hidden" accept=".jpg,.jpeg,.png" onChange={(e) => handleImageChange(e, true)} />
                    </label>
                  )}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setEditingEvent(null)} className="flex-1 border py-2 rounded-lg">{t('general.cancel')}</button>
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg">{t('general.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageEventsPage;
